import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Mail, Phone, Award, X } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrainersPage({ admin, onLogout }) {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    specialties: '',
    bio: '',
    hourly_rate: ''
  });

  useEffect(() => {
    fetchTrainers();
  }, []);

  const fetchTrainers = async () => {
    try {
      const response = await axios.get(`${API}/trainers`);
      setTrainers(response.data);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(Boolean),
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null
      };

      await axios.post(`${API}/trainers`, submitData);
      fetchTrainers();
      resetForm();
    } catch (error) {
      console.error('Error saving trainer:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      specialties: '',
      bio: '',
      hourly_rate: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <AdminLayout admin={admin} onLogout={onLogout} title="Gestion des Formateurs">
      <div className="space-y-6">
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 btn-violet"
            data-testid="add-trainer-button"
          >
            <Plus size={20} />
            Nouveau Formateur
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  {editingId ? 'Modifier le formateur' : 'Nouveau formateur'}
                </h3>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="trainer-firstname-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="trainer-lastname-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="trainer-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="trainer-phone-input"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Spécialités (séparées par des virgules)
                    </label>
                    <input
                      type="text"
                      value={formData.specialties}
                      onChange={(e) => setFormData({...formData, specialties: e.target.value})}
                      className="input-violet"
                      placeholder="Ex: Santé, Management, Communication"
                      data-testid="trainer-specialties-input"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Biographie</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({...formData, bio: e.target.value})}
                      className="input-violet"
                      rows="4"
                      data-testid="trainer-bio-input"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tarif horaire (€)</label>
                    <input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                      className="input-violet"
                      min="0"
                      step="0.01"
                      data-testid="trainer-rate-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="btn-violet flex-1" data-testid="save-trainer-button">
                    {editingId ? 'Mettre à jour' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : trainers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Aucun formateur</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trainers.map((trainer) => (
              <div key={trainer.id} className="card-violet" data-testid={`trainer-card-${trainer.id}`}>
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  {trainer.first_name} {trainer.last_name}
                </h3>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail size={16} className="text-violet-500" />
                    <span className="truncate">{trainer.email}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone size={16} className="text-violet-500" />
                    <span>{trainer.phone}</span>
                  </div>

                  {trainer.specialties && trainer.specialties.length > 0 && (
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <Award size={16} className="text-violet-500 mt-0.5" />
                      <div className="flex flex-wrap gap-1">
                        {trainer.specialties.map((spec, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs">
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {trainer.hourly_rate && (
                    <div className="text-sm font-semibold text-violet-600">
                      {trainer.hourly_rate} €/h
                    </div>
                  )}
                </div>

                {trainer.bio && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">{trainer.bio}</p>
                )}

                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-50 text-violet-600 rounded-lg hover:bg-violet-100 transition-colors">
                    <Edit size={16} />
                    Modifier
                  </button>
                  <button className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}